// @ts-nocheck - Class component types incompatible with React 18 + TS5
import './App.scss';

import * as Sentry from '@sentry/react';
import PrivateOutlet from 'common/PrivateOutlet';
import useAuth from 'common/useAuth';
import { Helmet, SuperadminToolbox } from 'components';
import SuspenseLoadingIndicator from 'components/LoadingPageIndicator/SuspenseLoadingIndicator';
import { lazyRetry } from 'helpers/lazyHelper';
import { DashboardPage } from 'pages/dashboard';
import { LoginPage } from 'pages/login';
import NotFoundPage from 'pages/NotFoundPage';
import { PublicOrderPage } from 'pages/orders';
import PublicOrderRequestPage from 'pages/publicOrderRequest/PublicOrderRequestPage';
import PublicReportPage from 'pages/publicReport/PublicReportPage';
import { PasswordPage } from 'pages/register';
import React, { lazy, Suspense, useEffect } from 'react';
import { Route, Routes, useLocation, useNavigationType } from 'react-router-dom';

import { EmailConfirm } from './pages/common';
import { CreatePassword, LostPassword, RecoveryPassword } from './pages/password';

const TOP_LEVEL_PATHNAMES = [
  '/',
  '/dashboard',
  '/orders',
  '/interventions',
  '/customers',
  '/request',
  '/serviceObjects',
  '/material',
  '/settings',
  '/customForms',
  '/user/settings',
  '/supportTickets',
  '/settings/users',
  '/admin/companies',
  '/admin/leads',
  '/admin/logs',
  '/admin/templates',
  '/admin/featureflags',
  '/admin/announcements',
  '/admin/supportTickets',
  '/admin/users',
  '/admin/customerUsers',
  '/admin/subscriptions'
];

const ScheduleAsyncPage = lazy(() => lazyRetry(() => import('./pages/schedule/SchedulePage')));

const OrdersAsyncPage = lazy(() => lazyRetry(() => import('./pages/orders/OrdersShell')));

const InterventionsAsyncPage = lazy(() =>
  lazyRetry(() => import('./pages/interventions/InterventionsPage'))
);

const RequestAsyncPage = lazy(() => lazyRetry(() => import('./pages/request/RequestShell')));

const CustomersAsyncPage = lazy(() => lazyRetry(() => import('./pages/customers/CustomersShell')));

const ServiceObjectsAsyncPage = lazy(() =>
  lazyRetry(() => import('./pages/serviceObjects/ServiceObjectsShell'))
);

const MaterialAsyncPage = lazy(() => lazyRetry(() => import('./pages/material/MaterialShell')));

const SettingsAsyncPage = lazy(() => lazyRetry(() => import('./pages/settings/SettingsShell')));

const SupportTicketsAsyncPage = lazy(() =>
  lazyRetry(() => import('./pages/support/SupportTicketsShell'))
);

const AdminAsyncPage = lazy(() => lazyRetry(() => import('./pages/admin/AdminShell')));

const AdminCompanySettingsPage = lazy(() =>
  lazyRetry(() => import('./pages/admin/companies/CompanySettingsShell'))
);

const UserSettingsPage = lazy(() =>
  lazyRetry(() => import('./pages/user/settings/UserSettingsShell'))
);

const CustomFormsPage = lazy(() => lazyRetry(() => import('./pages/customForms/CustomFormsShell')));

const App: React.FC = (): React.ReactElement => {
  const navigationType = useNavigationType();
  const location = useLocation();

  const { popUrlFromStack, pushUrlToStack, clearStack, user } = useAuth();

  useEffect(() => {
    if (navigationType === 'PUSH') {
      if (!TOP_LEVEL_PATHNAMES.includes(location.pathname)) {
        pushUrlToStack(location.pathname);
      } else {
        clearStack(location.pathname);
      }
    }

    if (navigationType === 'POP') {
      popUrlFromStack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <div className="App">
      {/* This Helmet providing default app title */}
      <Helmet />
      <Suspense fallback={<SuspenseLoadingIndicator />}>
        <Routes>
          {/* <PublicRoute
              path="/register"
              authenticated={authenticated}
              component={RegisterCompanyPage}
            /> */}
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/orderRequest/:tenantCode/:publicFormCode"
            element={<PublicOrderRequestPage />}
          />
          <Route
            path="/:tenantCode/report/:customerId/:orderCustomFormId/:reportId/:serviceObjectId"
            element={<PublicReportPage />}
          />
          <Route
            path="/:tenantCode/report/:customerId/:orderCustomFormId/:reportId"
            element={<PublicReportPage />}
          />
          <Route path="/:tenantCode/plan/:customerId/:orderId" element={<PublicOrderPage />} />
          <Route path="/register/:token/password/:userId" element={<PasswordPage />} />
          <Route path="/password/:token/recovery/:userId" element={<RecoveryPassword />} />
          <Route path="/password/:token/create/:userId" element={<CreatePassword />} />
          <Route path="/password" element={<LostPassword />} />
          <Route path="/emailconfirm/:userId/token/:token" element={<EmailConfirm />} />
          <Route path="/" element={<PrivateOutlet />}>
            <Route
              index
              element={user?.isSuperAdmin ? <DashboardPage /> : <ScheduleAsyncPage />}
              // element={user?.isSuperAdmin ? <DashboardPage /> : <SchedulePage />}
            />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/orders/*" element={<OrdersAsyncPage />} />
            <Route path="/interventions" element={<InterventionsAsyncPage />} />
            <Route path="/supportTickets/*" element={<SupportTicketsAsyncPage />} />
            <Route path="/request/*" element={<RequestAsyncPage />} />
            <Route path="/serviceObjects/*" element={<ServiceObjectsAsyncPage />} />
            <Route path="/customers/*" element={<CustomersAsyncPage />} />
            <Route path="/material/*" element={<MaterialAsyncPage />} />
            <Route path="/settings/*" element={<SettingsAsyncPage />} />
            <Route path="/admin/companies/:companyId/*" element={<AdminCompanySettingsPage />} />
            <Route path="/admin/*" element={<AdminAsyncPage />} />
            <Route path="/user/settings/*" element={<UserSettingsPage />} />
            <Route path="/customForms/*" element={<CustomFormsPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
      <SuperadminToolbox />
    </div>
  );
};

export default Sentry.withProfiler(App);
